/* eslint-disable no-restricted-syntax */
import { I18n } from 'react-redux-i18n';
import QRCodeConfigRequests from '../../server/QRCodeConfig';
import addNotification from '../../../components/notification';
import { addLoading, removeLoading } from './loading';

export const setQRCodeEAN = (value) => ({
  type: 'qr_code_EAN',
  payload: value,
});

export const setQRCodeLotNumber = (value) => ({
  type: 'qr_code_lot_number',
  payload: value,
});

export const setQRCodeExpirationDate = (value) => ({
  type: 'qr_code_expiration_date',
  payload: value,
});

export const setQRCodeManufacturingDate = (value) => ({
  type: 'qr_code_manufacturing_date',
  payload: value,
});

export const setQRCodeQuantity = (value) => ({
  type: 'qr_code_quantity',
  payload: value,
});

export const setQRCodeInitialProps = () => ({
  type: 'qr_code_initial_props',
});

export const getQRCodeConfig = (qrCodeConfigId) => async (dispatch) => {
  dispatch(addLoading());
  try {
    return await QRCodeConfigRequests.getQRCodeConfig(qrCodeConfigId);
  } finally {
    dispatch(removeLoading());
  }
};

export const getAllQRCodes = (qrCodeConfigId) => async (dispatch) => {
  dispatch(addLoading());
  try {
    return await QRCodeConfigRequests.getAllQRCodes(qrCodeConfigId);
  } finally {
    dispatch(removeLoading());
  }
};

export const getQRCodeConfigByCode = (qrCodeConfigCode) => async (dispatch) => {
  dispatch(addLoading());
  try {
    return await QRCodeConfigRequests.getQRCodeConfigByCode(qrCodeConfigCode);
  } finally {
    dispatch(removeLoading());
  }
};

export const createQRCodeConfig = (QRCodeConfig) => async (dispatch) => {
  dispatch(addLoading());
  try {
    return await QRCodeConfigRequests.createQRCodeConfig(QRCodeConfig);
  } finally {
    dispatch(removeLoading());
  }
};

export const updateQRCodeConfig = (QRCodeConfig) => async (dispatch) => {
  dispatch(addLoading());
  try {
    return await QRCodeConfigRequests.updateQRCodeConfig(QRCodeConfig);
  } finally {
    dispatch(removeLoading());
  }
};

export const deleteQRCodeConfig = (qrCodeConfigId) => async (dispatch) => {
  dispatch(addLoading());
  try {
    return await QRCodeConfigRequests.deleteQRCodeConfig(qrCodeConfigId);
  } finally {
    dispatch(removeLoading());
  }
};

const parseDateFromFormat = (value, format) => {
  if (typeof value !== 'string' || typeof format !== 'string') {
    return null;
  }

  const cleanValue = value.replace(/\D/g, '');

  const cleanFormat = format.toUpperCase().replace(/[^DMAY]/g, '');
  if (!cleanValue || !cleanFormat) {
    return null;
  }

  const groups = cleanFormat.match(/(D+|M+|A+|Y+)/g);
  if (!groups || !groups.length) {
    return null;
  }

  let index = 0;
  let day = '';
  let month = '';
  let year = '';

  for (const group of groups) {
    const { length } = group;
    const part = cleanValue.substr(index, length);

    switch (group[0]) {
      case 'D':
        day = part;
        break;

      case 'M':
        month = part;
        break;

      case 'A':
      case 'Y':
        year = part.length === 2 ? `20${part}` : part;
        break;

      default:
        break;
    }

    index += length;
  }

  day = day.padStart(2, '0');
  month = month.padStart(2, '0');

  return `${day}/${month}/${year}`;
};

const UDI_GROUP_SEPARATOR = '\u001d';

const UDI_AI_MAP = {
  '01': { fixed: 14 }, // EAN/GTIN
  10: { max: 20 }, // Número do lote
  11: { fixed: 6 }, // Data de fabricação
  17: { fixed: 6 }, // Data de validade
  30: { max: 8 }, // Quantidade variável
  37: { max: 8 }, // Quantidade de itens
};

const normalizeUdiCode = (qrCodeString) => {
  let value = String(qrCodeString).trim();

  if (value.charAt(0) === ']' && value.length >= 3) {
    value = value.slice(3);
  }

  return value.replace(/\{GS\}|<GS>/gi, UDI_GROUP_SEPARATOR);
};

const formatUdiExpirationDate = (value) => {
  if (typeof value !== 'string' || !/^\d{6}$/.test(value)) {
    return null;
  }

  const year = `20${value.substr(0, 2)}`;
  const month = value.substr(2, 2);
  let day = value.substr(4, 2);
  const monthNumber = Number(month);
  const dayNumber = Number(day);

  if (monthNumber < 1 || monthNumber > 12) {
    return null;
  }

  const lastDay = new Date(Number(year), monthNumber, 0).getDate();

  if (day === '00') {
    day = String(lastDay).padStart(2, '0');
  } else if (dayNumber < 1 || dayNumber > lastDay) {
    return null;
  }

  return `${day}/${month}/${year}`;
};

const assignUdiAiValue = (values, duplicated, ai, value) => {
  if (Object.prototype.hasOwnProperty.call(values, ai)) {
    if (values[ai] !== value) {
      duplicated[ai] = true;
    }
    return;
  }

  values[ai] = value;
};

const buildUdiResult = (values, duplicated) => {
  if (duplicated['01'] || duplicated['10'] || duplicated['17']) {
    return null;
  }

  const gtin = values['01'];

  if (!gtin || !/^\d{8,14}$/.test(gtin)) {
    return null;
  }

  return {
    ean: gtin,
    lotNumber: values['10'] ? values['10'] : null,
    expirationDate: values['17'] ? formatUdiExpirationDate(values['17']) : null,
    manufacturingDate: null,
    quantity: null,
  };
};

const matchUdiAi = (code, index) => {
  const ai = code.substr(index, 2);

  if (UDI_AI_MAP[ai]) {
    return {
      ai,
      rule: UDI_AI_MAP[ai],
    };
  }

  return null;
};

const parseParenthesizedUdi = (code) => {
  const regex = /\((\d{2,4})\)/g;
  const matches = [];
  let match = regex.exec(code);

  while (match) {
    matches.push({
      ai: match[1],
      valueStart: regex.lastIndex,
      tokenStart: match.index,
    });
    match = regex.exec(code);
  }

  if (!matches.length) {
    return null;
  }

  const values = {};
  const duplicated = {};

  for (let i = 0; i < matches.length; i += 1) {
    const current = matches[i];
    const next = matches[i + 1];
    const end = next ? next.tokenStart : code.length;
    let value = code.slice(current.valueStart, end).split(UDI_GROUP_SEPARATOR).join('').trim();

    if (current.ai === '01' || current.ai === '17') {
      value = value.replace(/\s/g, '');
    }

    assignUdiAiValue(values, duplicated, current.ai, value);
  }

  return buildUdiResult(values, duplicated);
};

const parseRawUdi = (code) => {
  const values = {};
  const duplicated = {};
  let index = 0;

  while (index < code.length) {
    if (code.charAt(index) === UDI_GROUP_SEPARATOR) {
      index += 1;
    } else {
      const matched = matchUdiAi(code, index);

      if (!matched) {
        break;
      }

      index += matched.ai.length;

      if (matched.rule.fixed) {
        if (index + matched.rule.fixed > code.length) {
          break;
        }

        assignUdiAiValue(
          values,
          duplicated,
          matched.ai,
          code.substr(index, matched.rule.fixed),
        );
        index += matched.rule.fixed;
      } else {
        const rest = code.slice(index);
        const gsIndex = rest.indexOf(UDI_GROUP_SEPARATOR);
        const rawValue = gsIndex >= 0 ? rest.slice(0, gsIndex) : rest;
        const value = rawValue.slice(0, matched.rule.max);

        assignUdiAiValue(values, duplicated, matched.ai, value);
        index += gsIndex >= 0 ? gsIndex + 1 : value.length;
      }
    }
  }

  if (!values['01']) {
    return null;
  }

  return buildUdiResult(values, duplicated);
};

const parseUdiCode = (qrCodeString) => {
  const fallback = {
    ean: qrCodeString,
    lotNumber: null,
    expirationDate: null,
    manufacturingDate: null,
    quantity: null,
  };

  if (!qrCodeString) {
    return fallback;
  }

  const normalized = normalizeUdiCode(qrCodeString);
  const parsed = normalized.indexOf('(') >= 0
    ? parseParenthesizedUdi(normalized)
    : parseRawUdi(normalized);

  if (!parsed) {
    return fallback;
  }

  return parsed;
};

const dispatchTranslatedQRCode = (dispatch, translated) => {
  dispatch(setQRCodeEAN(translated.ean || ''));
  dispatch(setQRCodeLotNumber(translated.lotNumber || null));
  dispatch(setQRCodeExpirationDate(translated.expirationDate));
  dispatch(setQRCodeManufacturingDate(translated.manufacturingDate));
  dispatch(setQRCodeQuantity(translated.quantity));
};

export const translateQRCode = (qrCodeString, qrCodeConfig) => async (dispatch) => {
  dispatch(setQRCodeInitialProps());
  try {
    if (qrCodeString && qrCodeConfig) {
      if (qrCodeConfig.usesUdiStandard) {
        const parsed = parseUdiCode(qrCodeString);
        dispatchTranslatedQRCode(dispatch, parsed);
        return parsed;
      }

      const splitModel = qrCodeString.split(qrCodeConfig.separator);
      if (splitModel.length === 1) {
        dispatch(setQRCodeEAN(splitModel[0]));
        return;
      }
      const expirationDateFormated = qrCodeConfig.expirationDatePosition
        ? parseDateFromFormat(
          splitModel[qrCodeConfig.expirationDatePosition - 1],
          qrCodeConfig.expirationDateFormat,
        )
        : null;

      const manufacturingDateFormated = qrCodeConfig.manufacturingDatePosition
        ? parseDateFromFormat(
          splitModel[qrCodeConfig.manufacturingDatePosition - 1],
          qrCodeConfig.manufacturingDateFormat,
        )
        : null;

      dispatch(setQRCodeEAN(splitModel[qrCodeConfig.productCodePosition - 1] || ''));
      dispatch(setQRCodeLotNumber(splitModel[qrCodeConfig.lotNumberPosition - 1] || null));
      dispatch(setQRCodeExpirationDate(expirationDateFormated));
      dispatch(setQRCodeManufacturingDate(manufacturingDateFormated));
      dispatch(setQRCodeQuantity(splitModel[qrCodeConfig.quantityPosition - 1] || null));
    }
  } catch (err) {
    addNotification(
      'danger',
      I18n.t('BEE4590' /* Configurador de QR Code */),
      I18n.t('BEE4606' /* Leitura inválida! */),
      'top-right',
    );
  }
};
